import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db7or1n3hiaolq192k30";

type Status = "idle" | "submitting" | "success" | "error";

interface ApiResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: ApiResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as ApiResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后再试",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试");
    }
  };

  return (
    <form
      data-readdy-form
      id="jmcomic2-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6"
    >
      <h2 className="font-heading font-bold text-lg text-foreground-950 mb-1.5 flex items-center gap-2">
        <i className="ri-chat-3-line text-primary-500" />
        意见反馈
      </h2>
      <p className="text-sm text-foreground-500 mb-5">
        对 JMcomic2 有任何建议或合作意向？留下你的信息，我们会尽快回复。
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-foreground-700 mb-1.5" htmlFor="fb-name">
            昵称 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs text-foreground-700 mb-1.5" htmlFor="fb-email">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
          />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs text-foreground-700 mb-1.5" htmlFor="fb-topic">
          反馈类型
        </label>
        <div className="relative">
          <select
            id="fb-topic"
            name="topic"
            defaultValue="意见反馈"
            className="w-full h-11 px-3 pr-9 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors appearance-none cursor-pointer"
          >
            <option value="意见反馈">意见反馈</option>
            <option value="内容合作">内容合作</option>
            <option value="版权申诉">版权申诉</option>
            <option value="功能建议">功能建议</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none" />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-xs text-foreground-700 mb-1.5" htmlFor="fb-message">
          反馈内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的建议或问题（最多 500 字）"
          className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
        />
      </div>

      <input
        className="form-note-alt"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-fill"} />
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        {status === "success" && (
          <span className="text-sm text-accent-500 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-fill" />
            提交成功，感谢你的反馈！
          </span>
        )}
        {status === "error" && (
          <span className="text-sm text-primary-500 flex items-center gap-1.5">
            <i className="ri-error-warning-fill" />
            {formError}
          </span>
        )}
      </div>
    </form>
  );
}